import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { jwtDecode } from "jwt-decode";
import { API_BASE_URL } from "../../config/api";

// Async thunk to call the login API
export const login = createAsyncThunk(
  "auth/login",
  async (credentials, { rejectWithValue }) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/agents/auth/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(credentials),
        }
      );

      const data = await res.json();
      
      if (!res.ok) return rejectWithValue(data);

      // Your response has: token, agent (with customer_id, is_temp_password)
      const { token, agent } = data;
      
      if (token) {
        localStorage.setItem("token", token);
        // Store customer ID and is_temp_password for quick access
        if (agent?.customer_id) {
          localStorage.setItem("customerId", agent.customer_id);
        }
        if (agent?.is_temp_password !== undefined) {
          localStorage.setItem("isTempPassword", agent.is_temp_password.toString());
        }
      }

      // Return the full response including agent data
      return data; // This returns { token, agent, status, message }
    } catch (err) {
      return rejectWithValue({ message: err.message || "Network error" });
    }
  }
);

const tokenFromStorage =
  typeof window !== "undefined" ? localStorage.getItem("token") : null;
const customerIdFromStorage =
  typeof window !== "undefined" ? localStorage.getItem("customerId") : null;
const isTempPasswordFromStorage =
  typeof window !== "undefined" ? localStorage.getItem("isTempPassword") : null;

const initialState = {
  user: tokenFromStorage ? jwtDecode(tokenFromStorage) : null,
  token: tokenFromStorage,
  customerId: customerIdFromStorage,
  isTempPassword: isTempPasswordFromStorage ? 
    isTempPasswordFromStorage === 'true' : false, // Convert string to boolean
  isAuthenticated: !!tokenFromStorage,
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout(state) {
      state.user = null;
      state.token = null;
      state.customerId = null;
      state.isTempPassword = false;
      state.isAuthenticated = false;
      state.error = null;
      if (typeof window !== "undefined") {
        localStorage.removeItem("token");
        localStorage.removeItem("customerId");
        localStorage.removeItem("isTempPassword");
      }
    },
    setUser(state, action) {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
    },
    setCustomerId(state, action) {
      state.customerId = action.payload;
    },
    // Reducer to update isTempPassword after password change
    setIsTempPassword(state, action) {
      state.isTempPassword = action.payload;
      if (typeof window !== "undefined") {
        if (action.payload !== undefined) {
          localStorage.setItem("isTempPassword", action.payload.toString());
        } else {
          localStorage.removeItem("isTempPassword");
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token || null;
        
        // Store the entire agent object if available
        state.user = action.payload.agent || null;
        
        // Store customer ID from the agent object
        if (action.payload.agent?.customer_id) {
          state.customerId = action.payload.agent.customer_id;
          
          // Store in localStorage
          if (typeof window !== "undefined") {
            localStorage.setItem("customerId", action.payload.agent.customer_id);
          }
        }
        
        // Store is_temp_password from the agent object
        if (action.payload.agent?.is_temp_password !== undefined) {
          state.isTempPassword = action.payload.agent.is_temp_password;
          
          // Store in localStorage
          if (typeof window !== "undefined") {
            localStorage.setItem(
              "isTempPassword", 
              action.payload.agent.is_temp_password.toString()
            );
          }
        }
        
        state.isAuthenticated = !!action.payload.token;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error;
      });
  },
});

export const { logout, setUser, setCustomerId, setIsTempPassword } = authSlice.actions;
export default authSlice.reducer;